import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MultiSelectFilter, RadioFilter, SelectFilter } from '@models/filter-types';
import { FilterOption, PoliticalRateCardPopupReturn } from '@models/rate-card/rate-card';
import {
    PoliticalWindowItem,
    ChannelItem,
    LookupItem,
    MarketItem,
    PoliticalInitializationTiersConfiguration,
    PoliticalWizardRow,
    StationItem,
    PoliticalInitializationTiers,
    StringLookupItem,
    PoliticalWindowInitializationItem,
    Grid,
} from '@models/lookup';
import { PoliticalRateCardFiltersV2 } from '@models/rate-card/political-rate-card-filters-v2';
import { CellEditingStartedEvent, CellEditingStoppedEvent, ColDef, GridReadyEvent } from 'ag-grid-community';
import { AgGridApiDirective } from '@shared/helpers/ag-grid/ag-grid';
import { getColumnDefs } from '@shared/helpers/ag-grid/ag-grid-builder';
import { CustomCellRenderers } from '@models/ag-grid';
import { SelectFilterCellComponent } from '@shared/components/ag-grid-components/cell-renderers/select-filter-cell/select-filter-cell.component';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { of } from 'rxjs';
import { RateCardService } from '@services/rate-card/rate-card.service';
import { NotificationService } from '@services/notification/notification.service';
import { CONFIRM_DIALOG, ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import moment from 'moment';
import { PushNotificationService } from '@services/push-notification/push-notification.service';
import { cloneDeep } from 'lodash';
import { AdminService } from '@services/admin/admin.service';
import { PoliticalWindowInitHistoryRow } from '@models/admin/admin';
import { UiConfig } from '@models/config';
import { AuthService } from '@services/auth/auth.service';
import { cascadeToFilter, cascadeToSingleFilter } from '@shared/helpers/functions/filter-helpers';

enum SetupTab {
    step1 = 0,
    step2 = 1,
}

enum RateStructureType {
    copyWindow = 'Copy Rates from another Political Window',
    defineRates = 'Define Rate Rules',
}

// Shown only when the selected window(s) are already initialized: re-run the last-used
// settings or define new rules.
enum RateSetupMode {
    setupNew = 'Set new rates',
    useExisting = 'Use the existing settings',
}

// Sub-choice under "Use the existing settings".
enum ExistingSettingsScope {
    all = 'Re-initialize all programs',
    onlyNew = 'Only initialize programs that are not set up yet',
}

@Component({
    selector: 'app-political-rate-card-popup',
    templateUrl: './political-rate-card-popup.component.html',
    styleUrls: ['./political-rate-card-popup.component.scss'],
    standalone: false,
})
export class PoliticalRateCardPopupComponent extends AgGridApiDirective<PoliticalWizardRow> implements OnInit {
    public marketItems: MarketItem[] = [];
    public stationItems: StationItem[] = [];
    public channelItems: ChannelItem[] = [];
    public stationIds: number[] = [];
    config!: UiConfig;
    spotLengths: MultiSelectFilter<LookupItem>;
    periodLevel: RadioFilter<FilterOption>;
    useLURFilter: SelectFilter<string>;
    lookupSvc!: LookupV2Service;
    showPcodes: boolean;

    setupTab = SetupTab;

    copyWindowSteps = ['STEP 1'];
    copyWindowForwardText = ['Set Rates'];
    definedRateSteps = ['STEP 1', 'STEP 2'];
    definedRateForwardText = ['Next', 'Set Rates'];
    reinitializeForwardText = ['Re-initialize'];

    initializedChannelIds: number[] = [];

    // Only rendered when the selected window(s)/channel(s) are already initialized.
    rateSetupModeFilter = new RadioFilter<FilterOption>('This Political Window has already been set up. How would you like to proceed?')
        .DisplayKey(true)
        .Vertical()
        .Options([
            { name: RateSetupMode.setupNew, filterFunc: () => false },
            { name: RateSetupMode.useExisting, filterFunc: () => false },
        ]);

    existingSettingsScopeFilter = new RadioFilter<FilterOption>('Which programs should be re-initialized?')
        .DisplayKey(true)
        .Vertical()
        .Options([
            { name: ExistingSettingsScope.onlyNew, filterFunc: () => false },
            { name: ExistingSettingsScope.all, filterFunc: () => false },
        ]);

    get steps(): string[] {
        if (this.useExistingSettings()) {
            return this.copyWindowSteps;
        }
        return this.isCopyWindow() ? this.copyWindowSteps : this.definedRateSteps;
    }

    get forwardText(): string[] {
        if (this.useExistingSettings()) {
            return this.reinitializeForwardText;
        }
        return this.isCopyWindow() ? this.copyWindowForwardText : this.definedRateForwardText;
    }

    // True when every selected channel is already initialized for the selected window(s),
    // the only case where re-running the existing settings is meaningful.
    get selectionAlreadyInitialized(): boolean {
        return this.channelIds?.length > 0 && this.channelIds.every((id) => this.initializedChannelIds.includes(id));
    }

    useExistingSettings(): boolean {
        return this.rateSetupModeFilter.Value?.name === RateSetupMode.useExisting && this.selectionAlreadyInitialized;
    }

    backwardText = ['Cancel', 'Back'];
    stepCount = 0;
    forwardValidation = [this.validStep1Filters.bind(this), this.validStep2Filters.bind(this)];

    isLoading = false;

    rows: PoliticalWizardRow[] = [];
    colDefs: ColDef[] = [];
    overLayNoRowsTemplate = 'Loading';

    gridData = {
        columns: [
            {
                name: 'Class of Time',
                field: 'tierCreated',
            },
            {
                name: 'Rate Multiplier',
                field: 'multiplier',
                isEditable: true,
            },
            {
                name: 'Rate Rule',
                field: 'tierUsed',
                isEditable: true,
            },
        ],
    };

    cellRenderers: CustomCellRenderers = {};
    startEditValue: unknown;
    cancelEdit = false;

    filters: PoliticalRateCardFiltersV2;
    channelIds: number[] = [];
    allTiers: PoliticalInitializationTiers[] = [];
    tierInitializationHistory: PoliticalWindowInitHistoryRow[] = [];

    rateProcessOption!: string;
    rateStructureType = RateStructureType;

    allPoliticalWindows: PoliticalWindowItem[] = [];

    politicalWindows = new MultiSelectFilter<PoliticalWindowItem>('Political Window to Initialize')
        .OnLoad((items: PoliticalWindowItem[]) => {
            this.politicalWindows.Options(items.filter((politicalWindow) => moment(politicalWindow.endDate).isSameOrAfter(moment())));
            this.politicalWindows.options.map((option) => {
                if (moment(option.startDate).isBefore(moment())) {
                    option.startDate = moment().format('YYYY-MM-DD');
                }
                return option;
            });
        })
        .OnChange((items: PoliticalWindowItem[]) => {
            const allMarketIds = items.flatMap((item) => item.marketIds);
            const marketsOptions = this.filters?.marketItems.filter((i) => allMarketIds.includes(i.id)) ?? [];
            cascadeToFilter(this.markets, marketsOptions);
        })
        .Transform((v: PoliticalWindowItem[]) => [].concat(...v.map((window) => window.windowIds)))
        .Icon('fas fa-bullhorn')
        .Filterable(true)
        .Required(true)
        .RestoreObjectReferences(false);

    copyPoliticalWindows = new SelectFilter<PoliticalWindowItem>('Political Window to Copy Rates from')
        .Icon('fas fa-bullhorn')
        .Filterable(true)
        .Required(true);

    markets = new MultiSelectFilter<MarketItem>('Markets')
        .Defer(() => of([]))
        .OnChange((items: MarketItem[]) => {
            const allMarketIds = items?.flatMap((item) => item.id);
            const stationsOptions = this.filters?.stationItems.filter((i) => allMarketIds.includes(i.marketId)) ?? [];
            cascadeToFilter(this.stations, stationsOptions);
        })
        .Transform((items: MarketItem[]) => items.map((item) => item.id))
        .Filterable(true)
        .Icon('fas fa-map-marked-alt')
        .Required(true)
        .InFilterSet(false);

    stations = new MultiSelectFilter<StationItem>('Stations')
        .Defer(() => of([]))
        .OnChange(() => {
            this.stationIds = this.stations.Value?.map((i) => i.id);
            this.channelIds = this.filters?.channelItems.filter((r) => this.stationIds.includes(r.stationId)).map((r) => r.id);
            if (this.channelIds?.length > 0) {
                const copyEligibleWindows: PoliticalWindowItem[] = [];
                this.allPoliticalWindows.forEach((politicalWindow) => {
                    if (this.markets.Value.some((market) => politicalWindow.marketIds.includes(market.id))) {
                        copyEligibleWindows.push(politicalWindow);
                    }
                });
                cascadeToSingleFilter(this.copyPoliticalWindows, copyEligibleWindows);

                const initializedChannels: number[] = [];
                this.politicalWindows.Value.forEach((politicalWindow) => {
                    politicalWindow.initializedChannelIds.forEach((channelId) => {
                        if (channelId && !initializedChannels.includes(channelId)) {
                            initializedChannels.push(channelId);
                        }
                    });
                });
                this.initializedChannelIds = initializedChannels;
                this.lookupSvcs.getPoliticalInitAllTiers(this.channelIds).subscribe((tiers) => {
                    if (initializedChannels.length === 0) {
                        tiers = tiers.filter((tier) => !tier.isPolitical);
                    }
                    this.allTiers = tiers;
                });

                const politicalWindowIds: number[] = [];
                this.politicalWindows.Value.forEach((windowMarketGroup) => {
                    politicalWindowIds.push(...windowMarketGroup.windowIds);
                });

                this.adminSvc
                    .getPoliticalInitTiersHistory(this.channelIds, politicalWindowIds)
                    .subscribe((grid: Grid<PoliticalWindowInitHistoryRow>) => {
                        this.tierInitializationHistory = grid.data;
                    });
                this.lookupSvcs
                    .getPoliticalInitPoliticalTiers(this.channelIds, politicalWindowIds)
                    .subscribe((grid: PoliticalInitializationTiersConfiguration[]) => {
                        this.rows = grid.map((item) => ({
                            tierCreated: {
                                id: item.id,
                                name: item.name,
                                isIssue: item.isIssue,
                                isPolitical: item.isPolitical,
                            },
                            multiplier: item?.multiplier === undefined ? 1 : item.multiplier,
                            tierUsed: this.findTierConfigUsed(item),
                        }));
                        if (grid.length > 0) {
                            this.useLURFilter.Value = grid[0]?.includeLUR ? 'Yes' : 'No';
                            if (grid[0]?.spotLengths) {
                                const spotLengths: LookupItem[] = [];
                                grid[0].spotLengths.forEach((sl) => {
                                    const spotLength = this.spotLengths.options.find((sl2) => sl2.id === sl);
                                    spotLengths.push(spotLength);
                                });
                                this.spotLengths.Value = spotLengths;
                            }
                            if (grid[0]?.periodType) {
                                const periodType = grid[0].periodType === 'Political Window' ? 'Window' : 'Week';
                                this.periodLevel.Value = this.periodLevel.options.find((level) => level.name === periodType);
                            }
                        }
                    });
            }
        })
        .Transform((items: StationItem[]) => items.map((item) => item.id))
        .Filterable(true)
        .Icon('fas fa-tower-cell')
        .Required(true)
        .InFilterSet(false);

    constructor(
        protected rateCardSvc: RateCardService,
        public dialogRef: MatDialogRef<PoliticalRateCardPopupComponent, PoliticalRateCardPopupReturn>,
        public changeDetectorRef: ChangeDetectorRef,
        private lookupSvcs: LookupV2Service,
        private adminSvc: AdminService,
        protected snackBar: NotificationService,
        protected dialog: MatDialog,
        public pushNotificationSvc: PushNotificationService,
        private authSvc: AuthService,
        @Inject(MAT_DIALOG_DATA)
        data: {
            filters: PoliticalRateCardFiltersV2;
            spotLengthsFilters: MultiSelectFilter<LookupItem>;
            periodLevel: RadioFilter<FilterOption>;
            useLURFilter: SelectFilter<string>;
            politicalWindowOptions: PoliticalWindowItem[];
            showPcodes: boolean;
        },
    ) {
        super();
        this.filters = data.filters;
        this.spotLengths = data.spotLengthsFilters;
        this.periodLevel = data.periodLevel;
        this.useLURFilter = data.useLURFilter;
        this.showPcodes = data.showPcodes;
        this.allPoliticalWindows = data.politicalWindowOptions;
        this.politicalWindows.Options(
            this.allPoliticalWindows.filter((window) => window.endDate > moment(new Date()).format('YYYY-MM-DD')),
        );
        this.seedInitialFilterValues();
    }

    private seedInitialFilterValues(): void {
        if (!(this.filters?.politicalWindows.Value.length > 0)) {
            this.politicalWindows.Value = [];
            return;
        }
        this.markets.setValueNoOnChange(this.filters.markets.Value);
        this.stations.setValueNoOnChange(this.filters.stations.Value);

        const politicalWindowNames = this.filters.politicalWindows.Value.map((window) => window.baseName);
        this.politicalWindows.Value = this.politicalWindows.options.filter((window) => politicalWindowNames.includes(window.baseName));
    }

    ngOnInit(): void {
        this.config = this.authSvc.getConfig();
        this.rateSetupModeFilter.Value = this.rateSetupModeFilter.options[0];
        this.existingSettingsScopeFilter.Value = this.existingSettingsScopeFilter.options[0];
        this.colDefs = getColumnDefs(this.gridData.columns, this.cellRenderers);

        this.pushNotificationSvc.getNotification().subscribe((notification) => {
            if (notification && notification?.title !== '') {
                const successNotificationTypes = [
                    'Political Initialization Complete',
                    'Political Reinitialization Complete',
                    'Political RateCard Reloaded',
                ];
                if (successNotificationTypes.includes(notification?.title)) {
                    this.setFiltersCloseWindowAndRefresh();
                } else if (
                    notification?.title === 'Political Initialization Failed' ||
                    notification?.title === 'Political Reinitialization Failed'
                ) {
                    console.log(notification?.data);
                    this.closeDialog();
                    this.snackBar.openSnackBar('There was a server internal error, please contact your administrator', 'error');
                } else {
                    this.closeDialog();
                    this.snackBar.openSnackBar('Error Initializing Political Rate-Card', 'error');
                }
                this.pushNotificationSvc.emptyNotification();
            }
        });
    }

    setFiltersCloseWindowAndRefresh() {
        const initializedFilterData: PoliticalRateCardPopupReturn = {
            markets: this.markets?.Value,
            channelGroups: this.stations?.Value[0].channelGroups,
            stations: this.stations?.Value,
            politicalWindowNames: this.politicalWindows.Value.map((i) => i.baseName),
            periodLevel: this.periodLevel.Value.name === 'Week' ? 'Week' : 'Political Window',
        };
        this.closeWindowAndRefresh(initializedFilterData);
        this.snackBar.openSnackBar('Initializing Political Rate-Card successfully', 'success');
    }

    onCellEditStart(event: CellEditingStartedEvent) {
        if (event.colDef.field === 'multiplier') {
            this.startEditValue = parseFloat(event.value);
        }
    }

    onGridReadyCustomizeWidth(params: GridReadyEvent) {
        const gridApi = params.api;
        const columnWidthMap: Record<string, number> = {
            tierCreated: 185,
            multiplier: 100,
            tierUsed: 280,
        };
        Object.keys(columnWidthMap).forEach((columnName) => {
            const column = gridApi.getColumn(columnName);
            if (column) {
                gridApi.setColumnWidths([{ key: columnName, newWidth: columnWidthMap[columnName] }]);
            }
        });
    }

    onCellEditStopped(event: CellEditingStoppedEvent): void {
        if (!this.cancelEdit) {
            if (event.colDef.field === 'multiplier' && event.data.tierUsed) {
                const value = parseFloat(event.value);
                if (isNaN(value)) {
                    this.snackBar.openSnackBar('Please enter a numeric value for the Rate Multiplier.', 'error');
                } else if (value !== this.startEditValue) {
                }
            }
        } else {
            this.cancelEdit = false;
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    tabChanged(event: number): void {
        this.stepCount += event;
        this.cellRenderers = {
            tierUsed: {
                cellRendererFramework: SelectFilterCellComponent,
                params: {
                    firstCascadeParent: 'tierCreated',
                    filterSource: (tierName: StringLookupItem) => of(this.allTiers.filter((item) => item.classOfTime !== tierName.name)),
                    clearable: true,
                    showTierGroup: true,
                    showPcodes: this.showPcodes,
                    compareFunction: (item: StringLookupItem, selected: StringLookupItem) =>
                        item && selected ? item.id === selected.id && item.name === selected.name : item === selected,
                },
            },
        };
        this.colDefs = getColumnDefs(this.gridData.columns, this.cellRenderers);
    }

    previousStep(): void {
        this.stepCount -= 1;
    }

    validStep1Filters() {
        if (this.useExistingSettings()) {
            // Existing settings come from the last initialization; no rules/spot lengths needed.
            return this.politicalWindows.Value.length > 0 && this.markets.Value.length > 0 && this.stations.Value.length > 0;
        }
        let copyWindowValid = true;
        if (this.isCopyWindow()) {
            copyWindowValid = this.copyPoliticalWindows.Value !== undefined;
        }
        return (
            this.politicalWindows.Value.length > 0 &&
            this.markets.Value.length > 0 &&
            this.stations.Value.length > 0 &&
            this.spotLengths.Value.length > 0 &&
            copyWindowValid
        );
    }

    validStep2Filters() {
        return true;
    }

    async populateRates(): Promise<void> {
        if (this.useExistingSettings()) {
            await this.reinitializeFromExistingSettings();
        } else if (this.isCopyWindow()) {
            this.populateRatesFromCopyWindow();
        } else {
            this.populateRatesFromDefinedStructure();
        }
    }

    async reinitializeFromExistingSettings(): Promise<void> {
        const subscription = await this.pushNotificationSvc.getSubscription();
        const onlyUninitialized = this.existingSettingsScopeFilter.Value?.name === ExistingSettingsScope.onlyNew;
        const body = {
            politicalWindows: this.getPoliticalWindowData(),
            onlyUninitialized,
            endpoint: subscription && subscription.endpoint ? subscription.endpoint : null,
            p256dh: subscription && subscription.keys ? subscription.keys.p256dh : null,
            auth: subscription && subscription.keys ? subscription.keys.auth : null,
        };

        this.isLoading = true;
        this.triggerNotificationMessage(subscription);
        this.rateCardSvc.reinitializePoliticalRateCard(body).subscribe(
            () => {
                if (subscription == null) {
                    this.setFiltersCloseWindowAndRefresh();
                }
            },
            (apiError) => {
                this.isLoading = false;
                if (apiError.status !== 504) {
                    if (typeof apiError?.error?.message === 'string' && apiError?.error?.message.includes('Subscription error')) {
                        this.setFiltersCloseWindowAndRefresh();
                    } else {
                        this.snackBar.openSnackBar('There was a server internal error, please contact your administrator', 'error');
                    }
                    this.pushNotificationSvc.emptyNotification();
                }
            },
        );
    }

    async checkAndGetData(): Promise<void> {
        if (!this.isValidTierAssignment()) {
            this.snackBar.openSnackBar('Tier configuration error, A rate rule for a tier cannot be set for the same tier.', 'error');
        } else if (this.hasCircularLogic()) {
            this.snackBar.openSnackBar('Tier configuration error. Please check the rate rule assignments.', 'error');
        } else {
            await this.getData();
        }
    }

    checkWhatChannelsCanBeInitialized() {
        let popUpMessage = '';
        const failingChannelTiers: Record<string, string[]> = {};
        const successfulChannelTiers: Record<string, string[]> = {};
        this.channelIds.forEach((channelId) => {
            const validInitializations = this.getValidRows();
            for (const initialization of validInitializations) {
                if (initialization.tierUsed) {
                    const channelHasTierInitialized = this.tierInitializationHistory.some(
                        (init) => init.channelId === channelId && init.tierInitializedId.toString() === initialization.tierUsed.id,
                    );
                    const channelName = this.filters.channelItems.find((channel) => channel.id === channelId).name;
                    const tierUsedName = initialization.tierUsed.name;
                    if (channelHasTierInitialized) {
                        if (!(tierUsedName in successfulChannelTiers)) {
                            successfulChannelTiers[tierUsedName] = [];
                        }
                        if (!successfulChannelTiers[tierUsedName].includes(channelName)) {
                            successfulChannelTiers[tierUsedName].push(channelName);
                        }
                    } else {
                        if (!(tierUsedName in failingChannelTiers)) {
                            failingChannelTiers[tierUsedName] = [];
                        }
                        if (!failingChannelTiers[tierUsedName].includes(channelName)) {
                            failingChannelTiers[tierUsedName].push(channelName);
                        }
                    }
                }
            }
        });
        if (Object.keys(successfulChannelTiers).length === 0) {
            this.snackBar.openSnackBar(
                'Rates do not exist for the selected Rate Rule during this Political window. \n' +
                    'Please set these rates or initialize using dynamic rates',
                'error',
            );
        } else if (Object.keys(failingChannelTiers).length > 0) {
            let channelMessage = '';
            Object.keys(failingChannelTiers).forEach((tier) => {
                channelMessage += tier + ': ' + failingChannelTiers[tier].join(', ') + '\n';
            });
            const channelStation = this.config.roleCapability.rate_optics?.general.channel ? 'channels' : 'stations';
            popUpMessage =
                `The following ${channelStation} do not have rates available for the chosen Rate Rule:\n` +
                `${channelMessage}\n` +
                `Would you like to initialize rates for all other selected ${channelStation}?`;
        }
        return popUpMessage;
    }

    populateRatesFromDefinedStructure(): void {
        const hasMissingDynamicTier = !this.rows.some((row) => row.tierUsed?.isDynamic || row.tierUsed?.isPCode);
        const hasNonNumericMultiplier = this.rows.some((row) => row.tierUsed && !this.isNumeric(row.multiplier));
        if (hasNonNumericMultiplier) {
            this.snackBar.openSnackBar('Please enter a numeric value for the Rate Multiplier.', 'error');
            return;
        }
        if (hasMissingDynamicTier) {
            const popUpMessage = this.checkWhatChannelsCanBeInitialized();
            if (popUpMessage) {
                this.createDialogForConfirmation(popUpMessage);
            } else {
                this.checkAndGetData();
            }
        } else {
            this.checkAndGetData();
        }
    }

    createDialogForConfirmation(message: string) {
        const dialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
                text: message,
                autoConfirm: false,
                noClickClose: true,
                confirmText: 'Yes',
                denyText: 'No',
                denyClose: true,
            },
        });

        dialog.afterClosed().subscribe((result) => {
            if (result === CONFIRM_DIALOG) {
                this.checkAndGetData();
            }
        });
    }

    populateRatesFromCopyWindow() {
        if (this.isSelfReferencingWindow()) {
            this.snackBar.openSnackBar('Window configuration error. Windows cannot be copied from themselves.', 'error');
        } else {
            this.getData();
        }
    }

    isSelfReferencingWindow(): boolean {
        if (
            this.politicalWindows.Value.some(
                (window) =>
                    window.baseName === this.copyPoliticalWindows.Value.baseName &&
                    window.startDate === this.copyPoliticalWindows.Value.startDate,
            )
        ) {
            return true;
        }
        return false;
    }

    hasCircularLogic(): boolean {
        const tiersToCheck: PoliticalWizardRow[] = [];
        const tiersInitializedWihPolitical = this.rows.filter((r) => r.tierUsed?.isPolitical);

        for (const tier of tiersInitializedWihPolitical) {
            tiersToCheck.push(tier);
            const visitedTiers = new Set<string>();
            while (tiersToCheck.length > 0) {
                const currentItem = tiersToCheck.pop();
                const currentTierIdUsed = currentItem.tierUsed.id;
                if (visitedTiers.has(currentTierIdUsed)) {
                    return true;
                }
                visitedTiers.add(currentTierIdUsed);
                const foundTierUsed = tiersInitializedWihPolitical.find((i) => this.getTierIdCreated(i) === currentTierIdUsed);
                if (foundTierUsed) {
                    tiersToCheck.push(foundTierUsed);
                }
            }
        }
        return false;
    }

    getTierIdCreated(tier: PoliticalWizardRow) {
        const tierId = String(tier.tierCreated.id);
        return tier.tierCreated.isIssue ? tierId + ' Issue' : tierId;
    }

    isValidTierAssignment(): boolean {
        for (const row of this.rows) {
            if (row.tierCreated?.id === row.tierUsed?.id && row.tierCreated.isPolitical === row.tierUsed.isPolitical) {
                return false;
            }
        }
        return true;
    }

    isCopyWindow(): boolean {
        return this.rateProcessOption === this.rateStructureType.copyWindow;
    }

    isNumeric(value: string | number): boolean {
        let num: number;
        if (typeof value == 'number') {
            num = value;
        } else {
            num = parseFloat(value);
        }
        return !isNaN(num) && isFinite(Number(value));
    }

    async getData(): Promise<void> {
        const subscription = await this.pushNotificationSvc.getSubscription();
        const spotLengthIdsData = Array.isArray(this.spotLengths.Value) ? this.spotLengths.Value.map((i) => i.id) : [];
        const politicalWindows = this.getPoliticalWindowData();

        let body = {
            politicalWindows,
            isCopyFromWindow: this.isCopyWindow(),
            endpoint: subscription && subscription.endpoint ? subscription.endpoint : null,
            p256dh: subscription && subscription.keys ? subscription.keys.p256dh : null,
            auth: subscription && subscription.keys ? subscription.keys.auth : null,
        };
        if (this.isCopyWindow()) {
            const copyFromWindowApi = this.getCopyPoliticalWindowData();
            const initializationTypeArgs = {
                copyFromWindow: copyFromWindowApi,
                useLUR: this.useLURFilter.Value === 'Yes',
            };
            body = { ...body, ...initializationTypeArgs };
        } else {
            const initializationTypeArgs = {
                spotLengthIds: spotLengthIdsData,
                periodLevel: this.periodLevel.Value.name,
                useLUR: this.useLURFilter.Value === 'Yes',
                initializationApproach: this.getValidRows(),
            };
            body = { ...body, ...initializationTypeArgs };
        }

        this.isLoading = true;
        this.triggerNotificationMessage(subscription);
        this.rateCardSvc.initializePoliticalRateCard(body).subscribe(
            () => {
                if (subscription == null) {
                    this.setFiltersCloseWindowAndRefresh();
                }
            },
            (apiError) => {
                this.isLoading = false;
                if (apiError.status !== 504) {
                    if (typeof apiError?.error?.message === 'string' && apiError?.error?.message.includes('Subscription error')) {
                        this.setFiltersCloseWindowAndRefresh();
                    } else {
                        this.snackBar.openSnackBar('There was a server internal error, please contact your administrator', 'error');
                    }
                    this.pushNotificationSvc.emptyNotification();
                }
            },
        );
    }

    getValidRows() {
        return this.rows.filter((i) => i?.tierUsed?.id);
    }

    getPoliticalWindowData(): object[] {
        const politicalWindowApiItems: PoliticalWindowItem[] = cloneDeep(this.politicalWindows.Value);
        politicalWindowApiItems.forEach((window) => {
            const commonStationIds = this.stations.Value.filter((ws) => window.marketIds?.includes(ws.marketId)).map((ws) => ws.id);
            window.channelIds = this.filters.channelItems.filter((ci) => commonStationIds.includes(ci.stationId)).map((ci) => ci.id);
        });
        const politicalWindowsData = politicalWindowApiItems.filter((window) => window.channelIds.length > 0);

        const splitPoliticalWindows: object[] = [];

        politicalWindowsData.forEach((windowGrouping) => {
            windowGrouping.windowIds.forEach((windowId) => {
                splitPoliticalWindows.push({
                    id: windowId,
                    startDate: windowGrouping.startDate,
                    endDate: windowGrouping.endDate,
                    name: windowGrouping.name,
                    channelIds: windowGrouping.channelIds,
                    marketIds: windowGrouping.marketIds,
                });
            });
        });
        return splitPoliticalWindows;
    }

    getCopyPoliticalWindowData(): PoliticalWindowInitializationItem {
        const copyPoliticalWindow: PoliticalWindowItem = cloneDeep(this.copyPoliticalWindows.Value);

        const commonStationIds = this.stations.Value.filter((ws) => copyPoliticalWindow.marketIds?.includes(ws.marketId)).map(
            (ws) => ws.id,
        );
        copyPoliticalWindow.channelIds = this.filters.channelItems
            .filter((ci) => commonStationIds.includes(ci.stationId))
            .map((ci) => ci.id);

        const politicalWindowApiItem = {
            id: copyPoliticalWindow.windowIds[0],
            startDate: copyPoliticalWindow.startDate,
            endDate: copyPoliticalWindow.endDate,
            name: copyPoliticalWindow.name,
            baseName: copyPoliticalWindow.baseName,
            channelIds: copyPoliticalWindow.channelIds,
        };

        return politicalWindowApiItem;
    }

    triggerNotificationMessage(subscription: PushSubscriptionJSON) {
        let notificationMessage = 'Political rates are currently initializing. (Note: This could take a few minutes)';
        if (subscription == null) {
            notificationMessage += this.pushNotificationSvc.blockedNotificationsMessage;
        }
        this.snackBar.openSnackBar(notificationMessage, 'info');
    }

    closeWindowAndRefresh(initializedFilterData: PoliticalRateCardPopupReturn): void {
        this.dialogRef.close(initializedFilterData);
    }

    findTierConfigUsed(item: PoliticalInitializationTiersConfiguration) {
        const tierType = item.configIsDynamic ? 'isDynamic' : item.configIsPCode ? 'isPCode' : 'isPolitical';
        if (item?.tierUsed === undefined) {
            return {
                id: null,
                name: null,
                isDynamic: null,
                isPolitical: null,
                isPCode: null,
            };
        }
        return this.allTiers.find((tier) => String(tier.id) === item.tierUsed && tier[tierType]);
    }
}
